import { BookmarkPlus, CalendarDays, Check, ChevronLeft, ChevronRight, ExternalLink, Play, Star, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, imageURL, year } from "../api";
import { track } from "../analytics";
import { useAuth } from "../auth";
import type { HomeSection, StreamingProvider, Title, WatchProviderResponse, WatchStatus } from "../types";

export function Hero({ title, onOpen }: { title: Title; onOpen(title: Title): void }) {
  const backdrop = imageURL(title.backdropPath, "backdrop");
  return (
    <section className="hero" style={backdrop ? { backgroundImage: `url(${backdrop})` } : undefined}>
      <div className="hero-shade" />
      <div className="hero-copy">
        <p className="eyebrow">오늘의 이야기 · Featured today</p>
        <h1>{title.name}</h1>
        {title.originalName && title.originalName !== title.name && <p className="original-title">{title.originalName}</p>}
        <div className="metadata">
          {year(title.releaseDate) && <span>{year(title.releaseDate)}</span>}
          <span>{title.type === "tv" ? "K-drama" : "K-film"}</span>
          {title.rating !== null && <span className="rating"><Star size={13} fill="currentColor" /> {title.rating.toFixed(1)}</span>}
        </div>
        <p className="hero-overview">{title.overview}</p>
        <div className="hero-actions">
          <button className="primary-button" onClick={() => { track("title_opened", { source: "hero", type: title.type }); onOpen(title); }}><Play size={17} fill="currentColor" /> Explore</button>
          <QuickAdd title={title} />
        </div>
      </div>
    </section>
  );
}

export function MediaRail({ section, onOpen }: { section: HomeSection; onOpen(title: Title): void }) {
  const railRef = useRef<HTMLDivElement>(null);
  const move = (direction: number) => railRef.current?.scrollBy({ left: direction * railRef.current.clientWidth * 0.82, behavior: "smooth" });
  if (section.items.length === 0) return null;
  return (
    <section className="media-section">
      <div className="section-heading">
        <div>
          <h2>{section.title}</h2>
          <p>{section.subtitle}</p>
        </div>
        <div className="rail-controls" aria-label={`Scroll ${section.title}`}>
          <button className="icon-button" onClick={() => move(-1)} aria-label="Scroll left"><ChevronLeft size={18} /></button>
          <button className="icon-button" onClick={() => move(1)} aria-label="Scroll right"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div className="media-rail" ref={railRef}>
        {section.items.map((title, index) => (
          <PosterCard key={title.id} title={title} index={section.id === "hot" ? index + 1 : undefined} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

export function PosterCard({ title, index, onOpen }: { title: Title; index?: number; onOpen(title: Title): void }) {
  const poster = imageURL(title.posterPath);
  return (
    <button className={`poster-card${index ? " ranked" : ""}`} onClick={() => { track("title_opened", { source: index ? "ranked_rail" : "media_rail", type: title.type }); onOpen(title); }} aria-label={`Open ${title.name}`}>
      {index && <span className="rank-number" aria-hidden="true">{index}</span>}
      <span className="poster-frame">
        {poster ? <img src={poster} alt="" width="500" height="750" loading="lazy" /> : <span className="poster-fallback">K</span>}
        <span className="poster-overlay">
          <span className="round-play"><Play size={17} fill="currentColor" /></span>
        </span>
      </span>
      <span className="poster-copy">
        <strong>{title.name}</strong>
        <span>{year(title.releaseDate)} · {title.type === "tv" ? "Drama" : "Film"}</span>
      </span>
    </button>
  );
}

export function TitleDialog({ title, onClose, onLibraryChange }: {
  title: Title;
  onClose(): void;
  onLibraryChange?(): void;
}) {
  const { user, openSignIn } = useAuth();
  const [status, setStatus] = useState<WatchStatus>("planned");
  const [isSaving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [region, setRegion] = useState(() => localStorage.getItem("klist.streamingRegion") ?? navigator.language.split("-")[1]?.toUpperCase() ?? "CA");
  const [providers, setProviders] = useState<WatchProviderResponse | null>(null);
  const [reminderDate, setReminderDate] = useState(title.nextAirDate ?? title.releaseDate);
  const [isEpisodeReminder, setEpisodeReminder] = useState(title.type === "tv" && Boolean(title.nextAirDate));
  const backdrop = imageURL(title.backdropPath, "backdrop");
  const poster = imageURL(title.posterPath);

  useEffect(() => {
    localStorage.setItem("klist.streamingRegion", region);
    setProviders(null);
    api<WatchProviderResponse>(`/v1/catalog/tmdb/${title.type}/${title.tmdbId}/watch-providers?region=${encodeURIComponent(region)}`)
      .then(setProviders)
      .catch(() => setProviders({ region, link: null, stream: [], free: [], rent: [], buy: [] }));
  }, [region, title.tmdbId, title.type]);

  useEffect(() => {
    api<{ title: Title }>(`/v1/catalog/titles/${title.id}`)
      .then((response) => {
        setReminderDate(response.title.nextAirDate ?? response.title.releaseDate);
        setEpisodeReminder(response.title.type === "tv" && Boolean(response.title.nextAirDate));
      })
      .catch(() => undefined);
  }, [title.id]);

  const add = async () => {
    if (!user) {
      onClose();
      openSignIn();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api(`/v1/me/library/titles/${title.id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });
      setSaved(true);
      onLibraryChange?.();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Could not save this title.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <article className="title-dialog" role="dialog" aria-modal="true" aria-labelledby="title-dialog-name">
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close title"><X size={19} /></button>
        <div className="title-backdrop" style={backdrop ? { backgroundImage: `url(${backdrop})` } : undefined}>
          <div className="title-backdrop-shade" />
        </div>
        <div className="title-dialog-content">
          {poster && <img className="detail-poster" src={poster} alt={`${title.name} poster`} width="500" height="750" />}
          <div className="title-copy">
            <p className="eyebrow">{title.type === "tv" ? "한국 드라마 · Korean drama" : "한국 영화 · Korean film"}</p>
            <h1 id="title-dialog-name">{title.name}</h1>
            {title.originalName && title.originalName !== title.name && <p className="original-title">{title.originalName}</p>}
            <div className="metadata">
              {year(title.releaseDate) && <span>{year(title.releaseDate)}</span>}
              {title.rating !== null && <span className="rating"><Star size={13} fill="currentColor" /> {title.rating.toFixed(1)}</span>}
              {title.seasonsCount && <span>{title.seasonsCount} season{title.seasonsCount === 1 ? "" : "s"}</span>}
              {title.runtimeMinutes && <span>{title.runtimeMinutes} min</span>}
            </div>
            {title.genres.length > 0 && <div className="genre-row">{title.genres.map((genre) => <span key={genre}>{genre}</span>)}</div>}
            <p className="detail-overview">{title.overview || "No synopsis is available yet."}</p>
            <section className="provider-panel">
              <div className="provider-heading"><div><h2>Where to watch</h2><p>Availability for {regionName(region)}</p></div><select value={region} onChange={(event) => { const value = event.target.value; track("streaming_region_changed", { region: value }); setRegion(value); }} aria-label="Streaming country">{providerRegions.map((code) => <option key={code} value={code}>{regionName(code)}</option>)}</select></div>
              {providers === null ? <div className="provider-loading" /> : [...providers.stream, ...providers.free, ...providers.rent, ...providers.buy].length === 0 ? <p className="provider-empty">No streaming, rental, or purchase options are currently listed here.</p> : <div className="provider-groups"><ProviderGroup label="Stream" providers={providers.stream} /><ProviderGroup label="Free" providers={providers.free} /><ProviderGroup label="Rent" providers={providers.rent} /><ProviderGroup label="Buy" providers={providers.buy} /></div>}
              <div className="provider-credit">Streaming data from JustWatch via TMDB.{providers?.link && <a href={providers.link} target="_blank" rel="noreferrer" onClick={() => track("streaming_offers_opened", { region, type: title.type })}>View current offers <ExternalLink size={12} /></a>}</div>
            </section>
            {reminderDate && new Date(`${reminderDate.slice(0, 10)}T12:00:00`).getTime() > Date.now() && <button className="secondary-button calendar-button" onClick={() => { track("release_reminder_downloaded", { kind: isEpisodeReminder ? "episode" : "release", type: title.type }); downloadCalendarReminder(title.name, reminderDate, isEpisodeReminder); }}><CalendarDays size={16} />Add {isEpisodeReminder ? "episode" : "release"} reminder</button>}
            <div className="save-row">
              <select value={status} onChange={(event) => setStatus(event.target.value as WatchStatus)} aria-label="Watch status">
                <option value="planned">Plan to watch</option>
                <option value="watching">Watching</option>
                <option value="watched">Watched</option>
                <option value="dropped">Dropped</option>
              </select>
              <button className="primary-button" onClick={() => void add()} disabled={isSaving || saved}>
                {saved ? <><Check size={17} /> Saved</> : <><BookmarkPlus size={17} /> {isSaving ? "Saving…" : "Add to Library"}</>}
              </button>
            </div>
            {error && <p className="form-error" role="alert">{error}</p>}
          </div>
        </div>
      </article>
    </div>
  );
}

function ProviderGroup({ label, providers }: { label: string; providers: StreamingProvider[] }) {
  if (providers.length === 0) return null;
  return <div className="provider-group"><span>{label}</span><div>{providers.map((provider) => <span className="provider-chip" key={provider.id}>{provider.logoPath ? <img src={`https://image.tmdb.org/t/p/w92${provider.logoPath}`} alt="" width="92" height="92" loading="lazy" /> : <Play size={15} />}<strong>{provider.name}</strong></span>)}</div></div>;
}

const providerRegions = ["CA", "US", "KR", "GB", "AU", "NZ", "IN", "SG", "PH", "MY", "ID", "JP", "FR", "DE", "ES", "IT", "NL", "SE", "NO", "DK", "FI", "BR", "MX"];
const regionNames = new Intl.DisplayNames(undefined, { type: "region" });
const regionName = (code: string) => regionNames.of(code) ?? code;

function downloadCalendarReminder(name: string, date: string, isEpisode: boolean) {
  const day = date.slice(0, 10).replaceAll("-", "");
  const safeName = name.replaceAll("\\", "\\\\").replaceAll(",", "\\,").replaceAll(";", "\\;").replaceAll("\n", "\\n");
  const event = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//My K-List//Release Reminder//EN",
    "BEGIN:VEVENT", `UID:klist-${crypto.randomUUID()}`, `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "")}`,
    `DTSTART;VALUE=DATE:${day}`, `SUMMARY:${isEpisode ? "New episode: " : "Release: "}${safeName}`,
    "BEGIN:VALARM", "TRIGGER:-PT12H", "ACTION:DISPLAY", `DESCRIPTION:${safeName}`, "END:VALARM",
    "END:VEVENT", "END:VCALENDAR", "",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([event], { type: "text/calendar;charset=utf-8" }));
  const anchor = document.createElement("a"); anchor.href = url; anchor.download = `${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-reminder.ics`; anchor.click();
  URL.revokeObjectURL(url);
}

function QuickAdd({ title }: { title: Title }) {
  const { user, openSignIn } = useAuth();
  const [saved, setSaved] = useState(false);
  const add = async () => {
    if (!user) return openSignIn();
    await api(`/v1/me/library/titles/${title.id}`, { method: "PUT", body: JSON.stringify({ status: "planned" }) });
    setSaved(true);
  };
  return (
    <button className="secondary-button" onClick={() => void add()} disabled={saved}>
      {saved ? <Check size={17} /> : <BookmarkPlus size={17} />}{saved ? "Saved" : "Add to Library"}
    </button>
  );
}

export function PageState({ title, message, action }: { title: string; message: string; action?: React.ReactNode }) {
  return <div className="page-state"><span className="state-mark">K</span><h1>{title}</h1><p>{message}</p>{action}</div>;
}

export function LoadingRows() {
  return <div className="loading-grid" aria-label="Loading"><span /><span /><span /><span /><span /><span /></div>;
}
